import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Ban,
  Bike,
  CheckCircle2,
  Handshake,
  Link2,
  Pencil,
  ShoppingBag,
  Store,
  Trash2,
  Users,
  Wallet,
} from "lucide-react";
import { Button, FormModal } from "@veorest/ui";
import {
  deleteAdminPartner,
  fetchAdminClients,
  fetchAdminPartner,
  linkAdminPartnerTenant,
  unlinkAdminPartnerClient,
  updateAdminClientType,
  updateAdminPartnerProfile,
  updateAdminPartnerStatus,
} from "@veorest/api-client";
import { ROUTES, clientDetailPath } from "../routes";
import { StatsStrip } from "../components/data/StatsStrip";
import { AdminWalletCard } from "../components/wallets/AdminWalletCard";
import { formatDate, formatIqd, formatNumber, statusPillClass } from "../lib/format";

function friendlyPartnerError(err: unknown): string {
  const m = (err instanceof Error ? err.message : String(err ?? "")).toLowerCase();
  if (m.includes("partner_slug_invalid")) return "Invalid slug: 3–48 chars (a-z, 0-9, dashes).";
  if (m.includes("partner_email_invalid")) return "Invalid contact email.";
  if (m.includes("partner_name_invalid")) return "Partner name must be 2–80 characters.";
  if (m.includes("partner_deleted")) return "This partner was deleted — it cannot be reactivated.";
  if (m.includes("partner_not_found")) return "Partner not found.";
  if (m.includes("wallet_dues_unsettled")) {
    return "Client has unsettled wallet dues (negative balance). Settle the client's wallet first — linking is blocked until the balance is settled.";
  }
  return err instanceof Error ? err.message : "Request failed";
}

export default function PartnerDetailPage({ id }: { id: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", slug: "", contactEmail: "" });

  const detail = useQuery({
    queryKey: ["admin-partner", id],
    queryFn: () => fetchAdminPartner(id),
    enabled: Boolean(id),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["admin-partner", id] });
    void qc.invalidateQueries({ queryKey: ["admin-partners"] });
  };

  const statusMutation = useMutation({
    mutationFn: (status: "active" | "suspended") => updateAdminPartnerStatus(id, status),
    onSuccess: () => {
      toast.success("Status updated");
      invalidate();
    },
    onError: (e: Error) => toast.error(friendlyPartnerError(e)),
  });

  const editMutation = useMutation({
    mutationFn: () =>
      updateAdminPartnerProfile(id, {
        name: editForm.name.trim(),
        slug: editForm.slug.trim().toLowerCase(),
        contactEmail: editForm.contactEmail.trim(),
      }),
    onSuccess: () => {
      toast.success("Partner updated");
      setEditOpen(false);
      invalidate();
    },
    onError: (e: Error) => toast.error(friendlyPartnerError(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteAdminPartner(id),
    onSuccess: (res) => {
      toast.success(
        `Partner deleted — ${res.removed_links} client link(s) removed, ${res.deactivated_users} account(s) deactivated`
      );
      setDeleteOpen(false);
      void qc.invalidateQueries({ queryKey: ["admin-partners"] });
      navigate(ROUTES.PARTNERS);
    },
    onError: (e: Error) => toast.error(friendlyPartnerError(e)),
  });

  const [linkOpen, setLinkOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClientId, setSelectedClientId] = useState("");

  const clientsQuery = useQuery({
    queryKey: ["admin-clients-link", clientSearch],
    queryFn: () => fetchAdminClients({ search: clientSearch, page: 1, limit: 50 }),
    enabled: linkOpen,
  });

  const linkableClients = (clientsQuery.data?.items ?? []).filter(
    (c) => c.client_type !== "partner" && !c.controlling_partner_id
  );

  /** SA links an EXISTING client only — the partner manages fees/dispatch from their dashboard. */
  const linkMutation = useMutation({
    mutationFn: async () => {
      if (!selectedClientId) throw new Error("Select a client first");
      await updateAdminClientType(selectedClientId, {
        clientType: "partner",
        controllingPartnerId: id,
        reason: "Linked from the partner view",
      });
      await linkAdminPartnerTenant(id, {
        tenantId: selectedClientId,
        dispatchAuthority: "partner",
        isPrimaryDispatcher: true,
      });
    },
    onSuccess: () => {
      toast.success("Client linked to partner");
      setLinkOpen(false);
      setSelectedClientId("");
      setClientSearch("");
      invalidate();
      void qc.invalidateQueries({ queryKey: ["admin-clients"] });
    },
    onError: (e: Error) => toast.error(friendlyPartnerError(e)),
  });

  const [unlinkTarget, setUnlinkTarget] = useState<{
    tenantId: string;
    tenantName: string;
  } | null>(null);
  const unlinkMutation = useMutation({
    mutationFn: async () => {
      if (!unlinkTarget) throw new Error("No client selected");
      await unlinkAdminPartnerClient(id, unlinkTarget.tenantId);
    },
    onSuccess: () => {
      toast.success("Client unlinked from partner");
      setUnlinkTarget(null);
      invalidate();
      void qc.invalidateQueries({ queryKey: ["admin-clients"] });
    },
    onError: (e: Error) => toast.error(friendlyPartnerError(e)),
  });

  const partner = detail.data?.partner;

  if (detail.isLoading) {
    return <div className="h-40 rounded-2xl skeleton" />;
  }

  if (!partner) {
    return (
      <div className="menu-page">
        <p className="text-[var(--text-muted)]">Partner not found.</p>
        <button className="btn-ghost mt-3" onClick={() => navigate(ROUTES.PARTNERS)}>
          Back
        </button>
      </div>
    );
  }

  const isSuspended = partner.status === "suspended";
  const isDeleted = partner.status === "deleted";
  const wallet = partner.wallet;
  const links = detail.data?.links ?? [];
  const users = detail.data?.users ?? [];

  return (
    <div className="menu-page">
      <header className="menu-page__header">
        <div className="min-w-0">
          <button className="btn-ghost mb-2" onClick={() => navigate(ROUTES.PARTNERS)}>
            <ArrowLeft className="w-4 h-4" /> All partners
          </button>
          <p className="menu-page__eyebrow">Partner</p>
          <h1 className="menu-page__title">{String(partner.name)}</h1>
          <p className="menu-page__subtitle flex items-center gap-2 flex-wrap">
            <span className={statusPillClass(String(partner.status))}>
              {String(partner.status)}
            </span>
            <span>{String(partner.slug)}</span>
            {partner.contact_email ? <span>{String(partner.contact_email)}</span> : null}
          </p>
        </div>
        <div className="menu-page__header-actions flex flex-wrap gap-2">
          <Button
            variant="secondary"
            className="min-h-[40px]"
            icon={<Pencil className="w-4 h-4" />}
            disabled={isDeleted}
            onClick={() => {
              setEditForm({
                name: String(partner.name || ""),
                slug: String(partner.slug || ""),
                contactEmail: String(partner.contact_email || ""),
              });
              setEditOpen(true);
            }}
          >
            Edit partner
          </Button>
          <Button
            variant="secondary"
            className="min-h-[40px]"
            icon={<Link2 className="w-4 h-4" />}
            disabled={isDeleted}
            onClick={() => {
              setSelectedClientId("");
              setClientSearch("");
              setLinkOpen(true);
            }}
          >
            Link client
          </Button>
          <Button
            variant="danger"
            className="min-h-[40px]"
            icon={<Trash2 className="w-4 h-4" />}
            disabled={isDeleted || deleteMutation.isPending}
            onClick={() => setDeleteOpen(true)}
          >
            Delete
          </Button>
          {isDeleted ? null : isSuspended ? (
            <Button
              className="min-h-[40px]"
              icon={<CheckCircle2 className="w-4 h-4" />}
              loading={statusMutation.isPending}
              onClick={() => statusMutation.mutate("active")}
            >
              Activate
            </Button>
          ) : (
            <Button
              variant="danger"
              className="min-h-[40px]"
              icon={<Ban className="w-4 h-4" />}
              loading={statusMutation.isPending}
              onClick={() => statusMutation.mutate("suspended")}
            >
              Suspend
            </Button>
          )}
        </div>
      </header>

      <StatsStrip
        tiles={[
          {
            key: "clients",
            label: "Linked clients",
            value: formatNumber(Number(partner.linked_clients_count ?? 0)),
            icon: Store,
            tone: "accent",
          },
          {
            key: "drivers",
            label: "Fleet drivers",
            value: formatNumber(Number(partner.active_driver_count ?? 0)),
            icon: Bike,
            tone: "info",
            caption: `${formatNumber(Number(partner.driver_count ?? 0))} total`,
          },
          {
            key: "orders",
            label: "Orders handled",
            value: formatNumber(Number(partner.handled_orders_count ?? 0)),
            icon: ShoppingBag,
            tone: "ink",
          },
          {
            key: "wallet",
            label: "Wallet balance",
            value: formatIqd(Number(wallet?.balance ?? 0)),
            icon: Wallet,
            tone: "success",
          },
        ]}
      />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-4">
        <section className="overview-panel xl:col-span-1">
          <header className="overview-panel__header">
            <div>
              <h3 className="overview-panel__title">Details</h3>
              <p className="overview-panel__subtitle">Partner organization profile</p>
            </div>
          </header>
          <div className="overview-panel__body p-4">
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-[var(--text-muted)] font-semibold">Slug</dt>
                <dd className="font-bold text-right">{String(partner.slug || "—")}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-[var(--text-muted)] font-semibold">Contact email</dt>
                <dd className="font-bold text-right break-all">
                  {String(partner.contact_email || "—")}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-[var(--text-muted)] font-semibold">Status</dt>
                <dd className="font-bold text-right">{String(partner.status)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-[var(--text-muted)] font-semibold">Created</dt>
                <dd className="font-bold text-right">{formatDate(String(partner.created_at))}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-[var(--text-muted)] font-semibold">Linked clients</dt>
                <dd className="font-bold text-right">
                  {formatNumber(Number(partner.linked_clients_count ?? 0))}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-[var(--text-muted)] font-semibold">Leave requests</dt>
                <dd className="font-bold text-right">
                  {Number(partner.leave_requests_count ?? 0) > 0 ? (
                    <span className="text-amber-600">
                      {formatNumber(Number(partner.leave_requests_count))}
                    </span>
                  ) : (
                    "0"
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </section>

        <section className="overview-panel xl:col-span-2">
          <header className="overview-panel__header">
            <div>
              <h3 className="overview-panel__title">Team accounts</h3>
              <p className="overview-panel__subtitle">Partner workspace logins</p>
            </div>
          </header>
          <div className="overview-panel__body">
            <div className="admin-table-wrap max-h-72 overflow-auto">
              <table className="table-flat">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-[var(--text-muted)] py-6">
                        No team accounts
                      </td>
                    </tr>
                  ) : (
                    users.map((u) => (
                      <tr key={u.user_id}>
                        <td className="break-all">{u.email || u.user_id}</td>
                        <td className="font-bold uppercase text-xs">{u.role}</td>
                        <td>
                          <span
                            className={
                              u.is_active
                                ? "rounded-full bg-green-50 px-2 py-0.5 text-xs font-bold text-green-700"
                                : "rounded-full bg-red-50 px-2 py-0.5 text-xs font-bold text-red-600"
                            }
                          >
                            {u.is_active ? "active" : "disabled"}
                          </span>
                        </td>
                        <td className="text-[var(--text-muted)]">{formatDate(u.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>

      <div className="mt-4">
        <AdminWalletCard
          walletType="partner"
          ownerId={id}
          title="Partner wallet (consolidated)"
          subtitle="Consolidated billing rail: covers OELOX per-trip fees for partner-managed trips + delivery settlements. Fee-only — food/order values never touch this wallet."
        />
      </div>

      <div className="mt-4">
        <section className="overview-panel">
          <header className="overview-panel__header">
            <div>
              <h3 className="overview-panel__title">Linked clients</h3>
              <p className="overview-panel__subtitle">
                Businesses under this partner — open a row to manage the client
              </p>
            </div>
          </header>
          <div className="overview-panel__body">
            <div className="admin-table-wrap max-h-96 overflow-auto">
              <table className="table-flat">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Link status</th>
                    <th>Linked at</th>
                    <th className="w-24" />
                  </tr>
                </thead>
                <tbody>
                  {links.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-[var(--text-muted)] py-6">
                        No linked clients
                      </td>
                    </tr>
                  ) : (
                    links.map((link) => (
                      <tr
                        key={link.tenant_id}
                        className="cursor-pointer"
                        onClick={() => navigate(clientDetailPath(link.tenant_id))}
                      >
                        <td>
                          <div className="admin-row-main">
                            <span className="admin-row-avatar" aria-hidden>
                              <Handshake className="h-4 w-4" strokeWidth={2.25} />
                            </span>
                            <span className="admin-row-title">{link.tenant_name}</span>
                          </div>
                        </td>
                        <td>
                          <span className={statusPillClass(link.status)}>{link.status}</span>
                        </td>
                        <td className="text-[var(--text-muted)]">
                          {link.linked_at ? formatDate(link.linked_at) : "—"}
                        </td>
                        <td className="text-end">
                          {link.status !== "removed" ? (
                            <Button
                              variant="ghost"
                              className="btn-sm text-[var(--danger)]"
                              disabled={unlinkMutation.isPending}
                              onClick={(e) => {
                                e.stopPropagation();
                                setUnlinkTarget({
                                  tenantId: link.tenant_id,
                                  tenantName: link.tenant_name,
                                });
                              }}
                            >
                              Unlink
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>

      <FormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit partner"
        subtitle="Organization name, slug and contact email."
        eyebrow="Partners"
        icon={<Users className="w-5 h-5" />}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setEditOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={editMutation.isPending || editForm.name.trim().length < 2}
              onClick={() => editMutation.mutate()}
            >
              {editMutation.isPending ? "Saving…" : "Save changes"}
            </button>
          </div>
        }
      >
        <div className="space-y-3 p-1">
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Partner name
            <input
              className="input-base mt-1"
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
            />
          </label>
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Slug
            <input
              className="input-base mt-1"
              value={editForm.slug}
              onChange={(e) => setEditForm((f) => ({ ...f, slug: e.target.value }))}
            />
          </label>
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Contact email
            <input
              className="input-base mt-1"
              value={editForm.contactEmail}
              onChange={(e) => setEditForm((f) => ({ ...f, contactEmail: e.target.value }))}
            />
          </label>
        </div>
      </FormModal>

      <FormModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete partner"
        subtitle="This deactivates all partner accounts and removes client links. The partner wallet history is kept."
        eyebrow="Danger zone"
        icon={<Trash2 className="w-5 h-5" />}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setDeleteOpen(false)}>
              Cancel
            </button>
            <Button
              variant="danger"
              loading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              Delete partner
            </Button>
          </div>
        }
      >
        <p className="p-1 text-sm text-[var(--text-secondary)]">
          Delete <span className="font-bold">{String(partner.name)}</span>? Team accounts will be
          deactivated, pending/active client links removed, and the partner can no longer sign in
          or dispatch.
        </p>
      </FormModal>

      <FormModal
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        title="Link existing client"
        subtitle="Move an existing client under this partner. Delivery fees, dispatch and wallet settings are managed by the partner from their own dashboard."
        eyebrow="Partners"
        icon={<Link2 className="w-5 h-5" />}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setLinkOpen(false)}>
              Cancel
            </button>
            <Button
              className="min-h-[40px]"
              loading={linkMutation.isPending}
              disabled={!selectedClientId}
              onClick={() => linkMutation.mutate()}
            >
              Link client
            </Button>
          </div>
        }
      >
        <div className="space-y-3 p-1">
          <input
            className="input-base"
            placeholder="Search clients by name…"
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
          />
          {clientsQuery.isLoading ? (
            <p className="text-sm text-[var(--text-muted)] p-2">Loading clients…</p>
          ) : linkableClients.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)] p-2">
              No unlinked clients found. New clients are created by the partner (passcode flow)
              from the partner dashboard.
            </p>
          ) : (
            <div className="max-h-72 space-y-1 overflow-auto">
              {linkableClients.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                    selectedClientId === c.id
                      ? "border-[var(--accent)] bg-[var(--accent-light)]"
                      : "border-[var(--border)] hover:bg-[var(--surface-muted)]"
                  }`}
                  onClick={() => setSelectedClientId(c.id)}
                >
                  <span className="font-bold">{c.name}</span>
                  {c.phone ? (
                    <span className="ml-2 text-[var(--text-muted)]">{c.phone}</span>
                  ) : null}
                </button>
              ))}
            </div>
          )}
          <p className="text-xs text-[var(--text-muted)]">
            Only existing clients can be linked. If the client has unsettled wallet dues, settle
            the balance first — linking is blocked until then.
          </p>
        </div>
      </FormModal>

      <FormModal
        open={unlinkTarget != null}
        onClose={() => {
          if (!unlinkMutation.isPending) setUnlinkTarget(null);
        }}
        title="Unlink client from partner"
        subtitle="The client becomes OELOX-direct again. Driver links and passcodes were already cleared when it was linked to this partner."
        eyebrow="Danger zone"
        icon={<Trash2 className="w-5 h-5" />}
        footer={
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setUnlinkTarget(null)}
            >
              Cancel
            </button>
            <Button
              variant="danger"
              loading={unlinkMutation.isPending}
              onClick={() => unlinkMutation.mutate()}
            >
              Unlink client
            </Button>
          </div>
        }
      >
        <p className="p-1 text-sm text-[var(--text-secondary)]">
          Unlink <span className="font-bold">{unlinkTarget?.tenantName}</span> from this partner?
        </p>
      </FormModal>
    </div>
  );
}
